import { useEffect } from "react";
import { motion, animate, useMotionValue, useTransform } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Users, CalendarCheck, UserX, AlertTriangle } from "lucide-react";

const container = {
  animate: { transition: { staggerChildren: 0.06 } },
};
const item = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
};

function CountUp({ value }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());
  useEffect(() => {
    const controls = animate(mv, Number(value) || 0, { duration: 0.8, ease: "easeOut" });
    return () => controls.stop();
  }, [value, mv]);
  return <motion.span>{rounded}</motion.span>;
}

function Kpi({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = {
    primary: { chip: "bg-teal-50 text-teal-600", bar: "bg-teal-500" },
    warn: { chip: "bg-amber-50 text-amber-600", bar: "bg-amber-500" },
    danger: { chip: "bg-rose-50 text-rose-600", bar: "bg-rose-500" },
  };
  const t = tones[tone] || tones.primary;
  return (
    <motion.div variants={item} whileHover={{ y: -3 }} transition={{ type: "spring", stiffness: 400, damping: 25 }}>
      <Card data-testid={testid} className="relative overflow-hidden p-5 border-slate-200/90 shadow-xs hover:shadow-md transition-shadow">
        <span className={`absolute left-0 top-0 bottom-0 w-1 ${t.bar}`} />
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-400">{label}</p>
            <p className="mt-2 text-3xl font-extrabold tabular-nums text-slate-900 font-heading tracking-tight">
              <CountUp value={value} />
            </p>
            {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
          </div>
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.chip}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default function KpiBar({ stats, batches = [] }) {
  if (!stats) return null;
  const totalCount = batches.reduce((s, b) => s + (b.count || 0), 0);
  const totalCap = batches.reduce((s, b) => s + (b.capacity || 0), 0);
  const tones = ["bg-teal-500", "bg-indigo-500", "bg-violet-500", "bg-sky-500"];
  return (
    <motion.div
      variants={container}
      initial="initial"
      animate="animate"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
    >
      <Kpi
        testid="kpi-running-patients"
        icon={Users}
        label="Running Patients"
        value={stats.total_running}
        sub={`${stats.total_completed} completed`}
        tone="primary"
      />
      <motion.div variants={item} whileHover={{ y: -3 }} transition={{ type: "spring", stiffness: 400, damping: 25 }}>
        <Card data-testid="kpi-batch-a-capacity" className="relative overflow-hidden p-5 border-slate-200/90 shadow-xs hover:shadow-md transition-shadow">
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
          <div className="flex items-start justify-between">
            <div className="w-full min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-400">Batch Load</p>
                <span className="text-xs tabular-nums text-slate-400">{totalCount}/{totalCap}</span>
              </div>
              <div className="mt-3 space-y-2 max-h-[92px] overflow-y-auto pr-1">
                {batches.length === 0 ? (
                  <p className="text-xs text-slate-400">No batches yet</p>
                ) : (
                  batches.map((b, i) => {
                    const pct = b.capacity ? Math.min(100, Math.round((b.count / b.capacity) * 100)) : 0;
                    const over = b.capacity && b.count > b.capacity;
                    return (
                      <div key={b.label} data-testid={`kpi-batch-load-${b.label}`}>
                        <div className="flex items-center justify-between text-[11px] mb-0.5">
                          <span className="flex items-center gap-1.5 font-medium text-slate-700">
                            <span className={`h-2 w-2 rounded-full ${tones[i % tones.length]}`} />
                            Batch {b.label}
                          </span>
                          <span className={`tabular-nums ${over ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                            {b.count}/{b.capacity}
                          </span>
                        </div>
                        <Progress value={pct} className="h-1.5" />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-indigo-50 text-indigo-600 ml-3 shrink-0">
              <CalendarCheck className="h-5 w-5" />
            </div>
          </div>
        </Card>
      </motion.div>
      <Kpi
        testid="kpi-not-visited-count"
        icon={UserX}
        label="Not Visited This Month"
        value={stats.not_visited}
        sub={`${stats.visited} visited`}
        tone="warn"
      />
      <Kpi
        testid="kpi-frequent-noshow-count"
        icon={AlertTriangle}
        label="Frequent No-Show"
        value={stats.frequent_noshow}
        sub={`≥ ${stats.threshold} missed`}
        tone="danger"
      />
    </motion.div>
  );
}
